import { useMemo } from 'react'

import { AgChartOptions } from 'ag-charts-community'

import { CustomAgChart } from '@fortem-monorepo/react-ag'

import { ICReportChartPrimitive } from '@/pages/funds/ic-report/components/primitives/types'
import { commodities } from '@/react-query/queries/ic-report/hooks'

export interface BearBullChartProps extends ICReportChartPrimitive {}

export const BearBullChart: React.FC<BearBullChartProps> = ({ params, options }) => {
  const query = commodities.useBearBull(params)
  const data = query.data

  const formattedChartData = useMemo(() => {
    return (data?.chart_data ?? []).map((item) => {
      const date = new Date(item.date)

      const bearData = data?.dates_data.bear ?? []
      const bullData = data?.dates_data.bull ?? []

      const bearPeriod = bearData.filter((period) => {
        const { st_dt, ed_dt } = {
          st_dt: new Date(period.st_dt),
          ed_dt: new Date(period.ed_dt),
        }

        return date >= st_dt && date <= ed_dt
      })?.[0]

      const bullPeriod = bullData.filter((period) => {
        const { st_dt, ed_dt } = {
          st_dt: new Date(period.st_dt),
          ed_dt: new Date(period.ed_dt),
        }

        return date >= st_dt && date <= ed_dt
      })?.[0]

      return {
        ...item,
        date,
        bearLabel: bearPeriod ? bearPeriod.label : null,
        bearLow: bearPeriod ? -1_000 : -1000,
        bearHigh: bearPeriod ? 1_000 : -1000,
        bullLabel: bullPeriod ? bullPeriod.label : null,
        bullLow: bullPeriod ? -1_000 : -1000,
        bullHigh: bullPeriod ? 1_000 : -1000,
      }
    })
  }, [data?.chart_data])

  const excludedKeys = ['date', 'bearLow', 'bearHigh', 'bullLow', 'bullHigh']

  const yKey = Object.keys(formattedChartData[0] ?? {}).filter(
    (key) => !excludedKeys.includes(key),
  )?.[0]

  const { maxValue } = useMemo(() => {
    return {
      // @ts-ignore
      minValue: Math.min(...formattedChartData.map((item) => item[yKey])),
      // @ts-ignore
      maxValue: Math.max(...formattedChartData.map((item) => item[yKey])),
    }
  }, [formattedChartData])

  const mergedOptions: AgChartOptions = {
    data: formattedChartData,
    series: [
      {
        type: 'line',
        xKey: 'date',
        yKey,
        yName: 'BCOM',
        stroke: 'darkorange',
        tooltip: {
          renderer: (data) => {
            return {
              title: data.datum.bearLabel ?? data.datum.bullLabel,
            }
          },
        },
        marker: {
          size: 0,
          fill: 'darkorange',
        },
      },
      {
        type: 'range-area',
        xKey: 'date',
        yLowKey: 'bearLow',
        yHighKey: 'bearHigh',
        yName: 'Bear',
        fill: 'red',
        stroke: 'red',
        fillOpacity: 0.15,
        marker: {
          strokeOpacity: 0,
          fill: 'red',
        },
      },
      {
        type: 'range-area',
        xKey: 'date',
        yLowKey: 'bullLow',
        yHighKey: 'bullHigh',
        yName: 'Bull',
        fill: 'green',
        stroke: 'green',
        fillOpacity: 0.15,
        marker: {
          strokeOpacity: 0,
          fill: 'green',
        },
      },
    ],
    axes: [
      {
        type: 'time',
        position: 'bottom',
        nice: false,
        // label: { format: "%b %y" },
      },
      { type: 'number', position: 'left', min: 0, max: maxValue + 0.25 },
    ],
    ...options,
  }
  return <CustomAgChart options={mergedOptions} />
}
